import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import { useState, useEffect, lazy, Suspense } from "react";
import { getApiUrl } from "./utils/apiUrl";

// Layout Components
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";

// Eager-loaded pages — public marketing surfaces
import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Subjects from "./pages/Subjects";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import GCSEMathsRevisionHub from "./pages/GCSEMathsRevisionHub";
import GCSEEnglishRevisionHub from "./pages/GCSEEnglishRevisionHub";
import GCSEScienceRevisionHub from "./pages/GCSEScienceRevisionHub";
import GCSERequiredPracticalsHub from "./pages/GCSERequiredPracticalsHub";
import FreeRevisionPlanner from "./pages/FreeRevisionPlanner";
import GCSEGradeBoundariesCalculator from "./pages/GCSEGradeBoundariesCalculator";
import GCSEMathsExaminerTips from "./pages/GCSEMathsExaminerTips";
import GCSEEnglishExaminerTips from "./pages/GCSEEnglishExaminerTips";
import GCSEScienceExaminerTips from "./pages/GCSEScienceExaminerTips";
import FreeResources from "./pages/FreeResources";
import FreeLesson from "./pages/FreeLesson";
import Contact from "./pages/Contact";
import Safeguarding from "./pages/Safeguarding";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import ForParents from "./pages/ForParents";
import ForPupils from "./pages/ForPupils";
import GetReady from "./pages/GetReady";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Welcome from "./pages/Welcome";
import VerifyEmail from "./pages/VerifyEmail";
import AdminContentPipeline from "./pages/AdminContentPipeline";

// Lazy-loaded pages — auth-gated / large bundles
const SubjectDetail = lazy(() => import("./pages/SubjectDetail"));
const LessonPage = lazy(() => import("./pages/LessonPage"));
const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const ParentDashboard = lazy(() => import("./pages/ParentDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminCMS = lazy(() => import("./pages/AdminCMS"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const AdminFeedback = lazy(() => import("./pages/AdminFeedback"));

const MathsHub = lazy(() => import("./pages/MathsHub"));
const MathsPractice = lazy(() => import("./pages/MathsPractice"));
const MathsProblemMode = lazy(() => import("./pages/MathsProblemMode"));

const EnglishHub = lazy(() => import("./pages/EnglishHub"));
const EnglishPastPapers = lazy(() => import("./pages/EnglishPastPapers"));
const EnglishSection1 = lazy(() => import("./pages/englishsection1"));
const EnglishSectionTest = lazy(() => import("./pages/englishsectiontest"));
const EnglishTopicPage = lazy(() => import("./pages/englishtopicpage"));

const BillingSuccess = lazy(() => import("./pages/BillingSuccess"));

const ScienceHub = lazy(() => import("./pages/ScienceHub"));
const ScienceDiscipline = lazy(() => import("./pages/ScienceDiscipline"));
const SciencePractice = lazy(() => import("./pages/SciencePractice"));

const GeographyHub = lazy(() => import("./pages/GeographyHub"));
const GeographyPractice = lazy(() => import("./pages/GeographyPractice"));

const ExaminerCheatSheet = lazy(() => import("./pages/ExaminerCheatSheet"));

const MockExamPlaceholder = lazy(() => import("./pages/MockExamPlaceholder"));
const MockExam = lazy(() => import("./pages/MockExam"));
const RequiredPracticalsHub = lazy(() => import("./pages/RequiredPracticalsHub"));
const Insights = lazy(() => import("./pages/Insights"));
const ExamHub = lazy(() => import("./pages/ExamHub"));
const Achievements = lazy(() => import("./pages/Achievements"));
const Diagnostic = lazy(() => import("./pages/Diagnostic"));

function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "parent") return <Navigate to="/parent/dashboard" replace />;

    const studentDest = location.pathname === "/signup" ? "/welcome" : "/dashboard";
    return <Navigate to={studentDest} replace />;
  }

  return children;
}

function SubjectGate({ children }) {
  const { user, loading } = useAuth();
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (loading) return;

    if (!user) {
      setCheckingAccess(false);
      return;
    }

    const plan = user.subscription?.plan;
    const isFounding = user.founding_member === true;
    const isPaid = plan && !["free"].includes(plan);
    const isAdmin = user.role === "admin";

    const trialActive = user.subscription?.trial_active === true;
    const trialEndsAt = user.subscription?.trial_ends_at;
    const trialNotExpired = trialEndsAt
      ? new Date(trialEndsAt).getTime() > Date.now()
      : false;
    const isOnActiveTrial = trialActive && trialNotExpired;

    if (isFounding || isPaid || isAdmin || isOnActiveTrial) {
      setHasAccess(true);
      setCheckingAccess(false);
      return;
    }

    if (user.role === "student") {
      fetch(`${getApiUrl()}/api/auth/me`, { credentials: "include" })
        .then((r) => r.json())
        .then((data) => {
          const sPlan = data?.subscription?.plan;
          const sTrialActive = data?.subscription?.trial_active === true;
          const sTrialEndsAt = data?.subscription?.trial_ends_at;
          const sTrialNotExpired = sTrialEndsAt
            ? new Date(sTrialEndsAt).getTime() > Date.now()
            : false;

          if ((sPlan && sPlan !== "free") || (sTrialActive && sTrialNotExpired)) {
            setHasAccess(true);
          }

          setCheckingAccess(false);
        })
        .catch(() => setCheckingAccess(false));
    } else {
      setCheckingAccess(false);
    }
  }, [user, loading]);

  if (user?.role === "parent") {
    return <Navigate to="/parent/dashboard" replace />;
  }

  if (loading || checkingAccess) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signup?role=parent" replace />;
  }

  if (hasAccess) {
    return children;
  }

  return <Navigate to="/pricing" replace />;
}

function GeographyExaminerTipsRoute() {
  return (
    <ExaminerCheatSheet
      config={{
        subjectKey: "geography",
        path: "/gcse-geography-examiner-tips",
        crumbLabel: "Geography examiner tips",
        seoTitle: "GCSE Geography Examiner Tips Cheat Sheet | Grade9Ready",
        seoDescription:
          "Free printable GCSE Geography examiner tips covering physical geography, human geography, fieldwork, map skills, common mistakes and Grade 9 moves.",
        h1: "GCSE Geography Examiner Tips Cheat Sheet",
        heroKicker: "Free GCSE Geography revision guide",
        heroLede:
          "A free printable Geography revision guide showing how marks are lost, what examiners want, the traps to avoid, and the higher-grade moves pupils can use in GCSE Geography answers.",
        appHubPath: "/subjects/geography",
        appHubLabel: "Geography",
        related: [
          {
            href: "/subjects/geography",
            kicker: "Live subject",
            title: "Explore GCSE Geography revision",
            testid: "geography-hub",
          },
          {
            href: "/free-revision-planner",
            kicker: "Free tool",
            title: "Build a GCSE revision plan",
            testid: "revision-planner",
          },
          {
            href: "/gcse-grade-boundaries-calculator",
            kicker: "Free tool",
            title: "GCSE grade boundaries calculator",
            testid: "grade-boundaries",
          },
        ],
      }}
    />
  );
}

function AppContent() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1">
        <Suspense
          fallback={
            <div
              className="min-h-[60vh] flex items-center justify-center"
              data-testid="route-suspense-fallback"
            >
              <div className="text-sm text-muted-foreground animate-pulse">Loading…</div>
            </div>
          }
        >
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/subjects" element={<Subjects />} />

            <Route path="/gcse-maths-revision" element={<GCSEMathsRevisionHub />} />
            <Route path="/gcse-english-revision" element={<GCSEEnglishRevisionHub />} />
            <Route path="/gcse-science-revision" element={<GCSEScienceRevisionHub />} />
            <Route path="/gcse-required-practicals" element={<GCSERequiredPracticalsHub />} />

            <Route path="/free-revision-planner" element={<FreeRevisionPlanner />} />
            <Route
              path="/gcse-grade-boundaries-calculator"
              element={<GCSEGradeBoundariesCalculator />}
            />

            <Route path="/gcse-maths-examiner-tips" element={<GCSEMathsExaminerTips />} />
            <Route path="/gcse-english-examiner-tips" element={<GCSEEnglishExaminerTips />} />
            <Route path="/gcse-science-examiner-tips" element={<GCSEScienceExaminerTips />} />
            <Route
              path="/gcse-geography-examiner-tips"
              element={<GeographyExaminerTipsRoute />}
            />

            <Route path="/free-resources" element={<FreeResources />} />
            <Route path="/free-lesson" element={<FreeLesson />} />

            <Route path="/pricing" element={<Pricing />} />
            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/safeguarding" element={<Safeguarding />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/for-parents" element={<ForParents />} />
            <Route path="/for-pupils" element={<ForPupils />} />
            <Route path="/get-ready" element={<GetReady />} />

            {/* Maths */}
            <Route
              path="/maths"
              element={
                <SubjectGate>
                  <MathsHub />
                </SubjectGate>
              }
            />
            <Route path="/maths/numbers/bodmas" element={<MathsPractice />} />
            <Route
              path="/maths/mock-exam"
              element={
                <SubjectGate>
                  <MockExam />
                </SubjectGate>
              }
            />
            <Route
              path="/maths/problem-mode"
              element={
                <SubjectGate>
                  <MathsProblemMode />
                </SubjectGate>
              }
            />
            <Route
              path="/maths/problem-mode/:tier"
              element={
                <SubjectGate>
                  <MathsProblemMode />
                </SubjectGate>
              }
            />
            <Route
              path="/maths/:sectionSlug/mock-exam"
              element={
                <SubjectGate>
                  <MockExam />
                </SubjectGate>
              }
            />
            <Route
              path="/maths/:sectionSlug/:topicSlug/mock-exam"
              element={
                <SubjectGate>
                  <MockExamPlaceholder />
                </SubjectGate>
              }
            />
            <Route
              path="/maths/:sectionSlug"
              element={
                <SubjectGate>
                  <MathsPractice />
                </SubjectGate>
              }
            />
            <Route
              path="/maths/:sectionSlug/:topicSlug"
              element={
                <SubjectGate>
                  <MathsPractice />
                </SubjectGate>
              }
            />

            {/* Science */}
            <Route
              path="/science"
              element={
                <SubjectGate>
                  <ScienceHub />
                </SubjectGate>
              }
            />
            <Route path="/science/biology/gcse-cell-biology" element={<SciencePractice />} />
            <Route
              path="/science/required-practicals"
              element={
                <SubjectGate>
                  <RequiredPracticalsHub />
                </SubjectGate>
              }
            />
            <Route
              path="/science/mock-exam"
              element={
                <SubjectGate>
                  <MockExam />
                </SubjectGate>
              }
            />
            <Route
              path="/science/combined/exam-practice"
              element={
                <SubjectGate>
                  <ExamHub />
                </SubjectGate>
              }
            />
            <Route
              path="/science/:disciplineSlug/:sectionSlug/mock-exam"
              element={
                <SubjectGate>
                  <MockExam />
                </SubjectGate>
              }
            />
            <Route
              path="/science/:disciplineSlug/:sectionSlug/:topicSlug/mock-exam"
              element={
                <SubjectGate>
                  <MockExamPlaceholder />
                </SubjectGate>
              }
            />
            <Route
              path="/science/:disciplineSlug"
              element={
                <SubjectGate>
                  <ScienceDiscipline />
                </SubjectGate>
              }
            />
            <Route
              path="/science/:disciplineSlug/:sectionSlug"
              element={
                <SubjectGate>
                  <SciencePractice />
                </SubjectGate>
              }
            />
            <Route
              path="/science/:disciplineSlug/:sectionSlug/:topicSlug"
              element={
                <SubjectGate>
                  <SciencePractice />
                </SubjectGate>
              }
            />

            {/* Geography — canonical live routes */}
            <Route
              path="/subjects/geography"
              element={
                <SubjectGate>
                  <GeographyHub />
                </SubjectGate>
              }
            />
            <Route
              path="/subjects/geography/locational-knowledge/our-world-continents-oceans-and-hemispheres"
              element={<GeographyPractice />}
            />
            <Route
              path="/subjects/geography/:sectionSlug"
              element={
                <SubjectGate>
                  <GeographyPractice />
                </SubjectGate>
              }
            />
            <Route
              path="/subjects/geography/:sectionSlug/:topicSlug"
              element={
                <SubjectGate>
                  <GeographyPractice />
                </SubjectGate>
              }
            />

            {/* Geography — legacy redirects */}
            <Route path="/geography" element={<Navigate to="/subjects/geography" replace />} />
            <Route
              path="/geography/:sectionSlug"
              element={<NavigateToSubjectsGeography />}
            />
            <Route
              path="/geography/:sectionSlug/:topicSlug"
              element={<NavigateToSubjectsGeography />}
            />

            {/* Generic subject detail route — keep after specific /subjects/geography routes */}
            <Route path="/subjects/:slug" element={<SubjectDetail />} />

            {/* Lessons */}
            <Route path="/lesson/:lessonId" element={<LessonPage />} />

            {/* English */}
            <Route
              path="/english"
              element={
                <SubjectGate>
                  <EnglishHub />
                </SubjectGate>
              }
            />
            <Route
              path="/english/understanding-fiction-and-non-fiction/different-types-of-text"
              element={<EnglishTopicPage />}
            />
            <Route
              path="/english/past-papers"
              element={
                <SubjectGate>
                  <EnglishPastPapers />
                </SubjectGate>
              }
            />
            <Route
              path="/english/past-papers/:slug"
              element={
                <SubjectGate>
                  <EnglishPastPapers />
                </SubjectGate>
              }
            />
            <Route
              path="/english/mock-exam"
              element={
                <SubjectGate>
                  <MockExam />
                </SubjectGate>
              }
            />
            <Route
              path="/english/:sectionSlug/mock-exam"
              element={
                <SubjectGate>
                  <MockExam />
                </SubjectGate>
              }
            />
            <Route
              path="/english/:sectionSlug/:topicSlug/mock-exam"
              element={
                <SubjectGate>
                  <MockExamPlaceholder />
                </SubjectGate>
              }
            />
            <Route
              path="/english/:sectionSlug"
              element={
                <SubjectGate>
                  <EnglishSection1 />
                </SubjectGate>
              }
            />
            <Route
              path="/english/:sectionSlug/test"
              element={
                <SubjectGate>
                  <EnglishSectionTest />
                </SubjectGate>
              }
            />
            <Route
              path="/english/:sectionSlug/:topicSlug"
              element={
                <SubjectGate>
                  <EnglishTopicPage />
                </SubjectGate>
              }
            />

            {/* Auth Routes */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/login"
              element={
                <AuthRoute>
                  <Login />
                </AuthRoute>
              }
            />
            <Route
              path="/signup"
              element={
                <AuthRoute>
                  <Signup />
                </AuthRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={["student"]}>
                  <StudentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/achievements"
              element={
                <ProtectedRoute allowedRoles={["student", "admin"]}>
                  <Achievements />
                </ProtectedRoute>
              }
            />
            <Route
              path="/insights"
              element={
                <ProtectedRoute allowedRoles={["student", "admin"]}>
                  <Insights />
                </ProtectedRoute>
              }
            />
            <Route
              path="/diagnostic"
              element={
                <ProtectedRoute allowedRoles={["student"]}>
                  <Diagnostic />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/content-pipeline"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminContentPipeline />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent/dashboard"
              element={
                <ProtectedRoute allowedRoles={["parent"]}>
                  <ParentDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/cms"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminCMS />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedback"
              element={
                <ProtectedRoute allowedRoles={["student", "parent"]}>
                  <FeedbackPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/feedback"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AdminFeedback />
                </ProtectedRoute>
              }
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />
      <Toaster />
    </div>
  );
}

function NavigateToSubjectsGeography() {
  const { pathname } = useLocation();
  const newPath = pathname.replace(/^\/geography/, "/subjects/geography");

  return <Navigate to={newPath} replace />;
}

function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });

      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    });

    return () => window.cancelAnimationFrame(id);
  }, [pathname, search]);

  return null;
}

function GAPageView() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;

    window.gtag("event", "page_view", {
      page_path: pathname + search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search]);

  return null;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <GAPageView />
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;